import { isNil } from "@etudescarre/utils";
import { formatDay } from "@/lib/utils/date";

export type YardStateInput = {
  isLocked: boolean;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  comissioningDate: string | null;
};

export type YardState =
  | "preparation"
  | "en_service"
  | "interrompu"
  | "archive"
  | "cloture";

/** End of the archiving period: the date the yard becomes « Clôturé ». */
function getArchivingEnd(
  expirationDate: string,
  beforeArchivingDays: number | null,
): Date {
  const archivingEnd = new Date(expirationDate);
  archivingEnd.setUTCDate(
    archivingEnd.getUTCDate() + (beforeArchivingDays ?? 0),
  );
  return archivingEnd;
}

export function getYardState(yard: YardStateInput): YardState {
  const now = new Date();

  if (isNil(yard.comissioningDate)) {
    return "preparation";
  }

  if (!isNil(yard.expirationDate)) {
    const expiration = new Date(yard.expirationDate);
    if (expiration < now) {
      const archivingEnd = getArchivingEnd(
        yard.expirationDate,
        yard.beforeArchivingDays,
      );
      if (archivingEnd < now) {
        return "cloture";
      }
      return "archive";
    }
  }

  return yard.isLocked ? "interrompu" : "en_service";
}

/**
 * The yard state with its key date (« En service jusqu'au 31/12/2026 »,
 * « Archivé jusqu'au … », « Clôturé le … »), or null when the yard is in
 * preparation or has no expiration date.
 */
export function getYardStateDateLabel(yard: YardStateInput): string | null {
  if (isNil(yard.expirationDate)) return null;
  const expiration = formatDay(yard.expirationDate);
  const archivingEnd = formatDay(
    getArchivingEnd(yard.expirationDate, yard.beforeArchivingDays),
  );

  switch (getYardState(yard)) {
    case "preparation":
      return null;
    case "en_service":
      return `En service jusqu'au ${expiration}`;
    case "interrompu":
      return `Interrompu — en service jusqu'au ${expiration}`;
    case "archive":
      return `Archivé jusqu'au ${archivingEnd}`;
    case "cloture":
      return `Clôturé le ${archivingEnd}`;
  }
}

/** Badge colours for a yard state, shared by the staff home and admin headers. */
export const yardStateBadgeClass: Record<YardState, string> = {
  preparation: "bg-zinc-100 text-zinc-600",
  en_service: "bg-green-100 text-green-700",
  interrompu: "bg-orange-100 text-orange-700",
  archive: "bg-yellow-100 text-yellow-700",
  cloture: "bg-red-100 text-red-700",
};

export type YardToggleIntent =
  | { kind: "needs_dates" }
  | { kind: "commission" }
  | { kind: "interrupt" }
  | { kind: "resume" }
  | { kind: "cloture_info" };

/**
 * Whether the ON/OFF toggle should display as ON for a given yard.
 *
 * The toggle reflects the derived state, not the raw isLocked flag:
 * terminal/non-commissioned states display OFF even if the yard happens
 * to be unlocked. During the archiving period the lock still controls
 * download access, so the toggle follows isLocked.
 */
export function isYardToggleOn(yard: YardStateInput): boolean {
  const state = getYardState(yard);
  if (state === "en_service") return true;
  if (state === "archive") return !yard.isLocked;
  return false;
}

/**
 * What clicking the ON/OFF toggle should do for a given yard,
 * derived from the yard state machine.
 */
export function getYardToggleIntent(yard: YardStateInput): YardToggleIntent {
  const state = getYardState(yard);

  switch (state) {
    case "preparation":
      if (isNil(yard.expirationDate) || isNil(yard.beforeArchivingDays)) {
        return { kind: "needs_dates" };
      }
      return { kind: "commission" };
    case "en_service":
      return { kind: "interrupt" };
    case "interrompu":
      return { kind: "resume" };
    case "archive":
      return yard.isLocked ? { kind: "resume" } : { kind: "interrupt" };
    case "cloture":
      return { kind: "cloture_info" };
  }
}
